import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { Calendar, MapPin, ArrowLeft, GraduationCap } from 'lucide-react';
import SEO from '@/components/SEO';
import { LocationAlertSignup } from '@/components/LocationAlertSignup';
import { resolveState } from '@/lib/usStates';

const EVENT_TYPE_LABEL = {
  teaching: 'Class',
  casual: 'Open Play',
  tournament: 'Tournament',
  special: 'Special Event',
};

/**
 * State-scoped event hub — `/events/california`, `/events/georgia`,
 * etc. Built for SEO long-tail rankings on "Mahjong events in
 * <State>", "Mahjong tournament <State>", "Mahjong class <State>".
 * Every entry is a crawlable `<Link>` to the event's `/events/:id`.
 */
export default function EventsByStatePage() {
  // Route can mount this as `/events/:eventId` (via dispatcher) or
  // `/events/:state` (direct). Accept either.
  const params = useParams();
  const stateParam = params.state || params.eventId || '';
  const stateInfo = resolveState(stateParam);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!stateInfo) { setLoading(false); return; }
    setLoading(true);
    api.get(`/events/by-state/${stateInfo.slug}`)
      .then(r => setEvents(r.data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [stateInfo]);

  if (!stateInfo) return <Navigate to="/events" replace />;
  if (stateParam !== stateInfo.slug) {
    return <Navigate to={`/events/${stateInfo.slug}`} replace />;
  }

  const title = `American Mahjong Events in ${stateInfo.name}`;
  const description = `${events.length} upcoming American Mahjong ${events.length === 1 ? 'event' : 'events'} in ${stateInfo.name}: classes, open play, tournaments, and charity events. Find local Mahjong games near you on Meet For Mahjong.`;

  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: title,
    numberOfItems: events.length,
    itemListElement: events.map((ev, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `https://meetformahjong.com/events/${ev.event_id}`,
      name: ev.title,
    })),
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="events-by-state-page">
      <SEO
        title={title}
        description={description}
        path={`/events/${stateInfo.slug}`}
        jsonLd={itemListJsonLd}
      />

      <div className="mb-6">
        <Link
          to="/events"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#56c6df]"
          data-testid="state-hub-back-to-all-events"
        >
          <ArrowLeft className="w-4 h-4" /> All events
        </Link>
      </div>

      <div className="text-center mb-10">
        <div className="inline-block px-3 py-1 rounded-full bg-[#56c6df]/10 text-[#0f766e] text-xs font-semibold tracking-wider uppercase mb-3">
          {stateInfo.name}
        </div>
        <h1
          className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {title}
        </h1>
        <p className="text-slate-500 max-w-2xl mx-auto leading-relaxed">
          {events.length > 0
            ? `${events.length} upcoming American Mahjong ${events.length === 1 ? 'event' : 'events'} in ${stateInfo.name} — classes, open play, tournaments, and charity events. Tap any event for full details, location, and how to RSVP.`
            : `No American Mahjong events are scheduled in ${stateInfo.name} right now — but Meet For Mahjong is growing fast. Sign up free to be notified when a new event is posted in your area, or post your own.`}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-[#56c6df] border-t-transparent rounded-full" />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12" data-testid="state-hub-events-empty-state">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to={`/instructors/${stateInfo.slug}`}>
              <Button className="rounded-full bg-[#b456df] text-white hover:bg-[#9a3ec4]">
                See instructors in {stateInfo.name}
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="rounded-full">Sign up free</Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="state-hub-events-list">
          {events.map(ev => {
            const dateLabel = ev.event_date
              ? new Date(ev.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
              : '';
            const locText = [ev.city, ev.state].filter(Boolean).join(', ');
            const typeLabel = EVENT_TYPE_LABEL[ev.event_type] || 'Mahjong Event';
            return (
              <Card
                key={ev.event_id}
                className="border-slate-100 hover:shadow-md transition-all"
                data-testid={`state-hub-event-${ev.event_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-wrap items-baseline gap-2 text-xs text-slate-500 mb-1">
                    <span className="px-2 py-0.5 rounded-full bg-[#56c6df]/15 text-[#0f766e] font-medium">
                      {typeLabel}
                    </span>
                    {ev.is_charity && (
                      <span className="px-2 py-0.5 rounded-full bg-pink-50 text-pink-700 font-medium">Charity</span>
                    )}
                    {ev.is_free && (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">Free</span>
                    )}
                    {dateLabel && <span>{dateLabel}</span>}
                    {ev.start_time && <span>· {ev.start_time}</span>}
                  </div>
                  <Link
                    to={`/events/${ev.event_id}`}
                    className="text-lg font-semibold hover:text-[#56c6df]"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {ev.title}
                  </Link>
                  <div className="flex items-center gap-3 text-sm text-slate-500 mt-1 flex-wrap">
                    {locText && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {ev.venue_name ? `${ev.venue_name}, ${locText}` : locText}
                      </span>
                    )}
                    {ev.host_name && ev.host_id && (
                      <Link
                        to={`/player/${ev.host_id}`}
                        className="text-slate-500 hover:text-[#b456df]"
                        data-testid={`state-hub-event-host-${ev.event_id}`}
                      >
                        Hosted by {ev.host_name}
                      </Link>
                    )}
                  </div>
                  {ev.description && (
                    <p className="text-sm text-slate-600 mt-2 line-clamp-2">{ev.description}</p>
                  )}
                  <div className="mt-3">
                    <Link to={`/events/${ev.event_id}`}>
                      <Button size="sm" className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] text-xs">
                        View event details →
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="mt-10 text-center">
        <Link
          to={`/instructors/${stateInfo.slug}`}
          className="inline-flex items-center gap-2 text-sm text-[#b456df] hover:underline"
          data-testid="state-hub-cross-link-instructors"
        >
          <GraduationCap className="w-4 h-4" />
          Find American Mahjong instructors in {stateInfo.name} →
        </Link>
      </div>

      <LocationAlertSignup
        type="event"
        stateSlug={stateInfo.slug}
        locationLabel={stateInfo.name}
      />
    </div>
  );
}
